import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  IconButton,


} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import dayjs from "dayjs";

const TransactionTable = () => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentUser, setCurrentUser] = useState(null);

  // sorting state
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  // ✅ Get user from localStorage
  useEffect(() => {
    const getUserFromLocalStorage = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          return parsedUser;
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
        showSnackbar("Error loading user data", "error");
      }
      return null;
    };

    const user = getUserFromLocalStorage();
    if (user) {
      fetchData(user);
    } else {
      showSnackbar("User not found in localStorage", "error");
    }
  }, []);

  const fetchData = async (user = null) => {
    setLoading(true);
    try {
      const userToUse = user || currentUser;
      if (!userToUse) {
        showSnackbar("User information not available", "error");
        setLoading(false);
        return;
      }

      const username = userToUse.username || userToUse.email;

      const res = await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/admin_translist",
        { transaction_user: username }
      );

      const items = res.data?.["body-json"]?.body?.transaction?.Items || [];
      setTransactions(items);
      setAllTransactions(items);
      if (items.length === 0) showSnackbar("No transactions found", "info");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to fetch transactions", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 Search
  const handleSearch = async () => {
    if (!searchQuery.trim())
      return showSnackbar("Enter a search term", "warning");

    if (!currentUser) {
      showSnackbar("User information not available", "error");
      return;
    }

    setLoading(true);
    try {
      const username = currentUser.username || currentUser.email || "";

      const res = await axios.get(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/trans_search",
        { params: { key: searchQuery.trim(), key1: username } }
      );

      const items = res.data?.body?.Items || [];
      setTransactions(items);
      setActiveSearch(searchQuery.trim());
      setPage(0);
      showSnackbar(`Found ${items.length} result(s)`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Search failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchQuery("");
    setActiveSearch("");
    setTransactions(allTransactions);
    setPage(0);
    showSnackbar("Search cleared", "info");
  };

  // 🧮 Sorting (Fixed for DD/MM/YYYY format)
  const handleSort = (field) => {
    let order = "asc";
    if (sortField === field && sortOrder === "asc") order = "desc";

    const sorted = [...transactions].sort((a, b) => {
      const valA = a[field] || "";
      const valB = b[field] || "";

      // ✅ Handle date sorting (DD/MM/YYYY)
      if (field === "date" || field === "time") {
        const combinedA = `${a.date || ""} ${a.time || ""}`.trim();
        const combinedB = `${b.date || ""} ${b.time || ""}`.trim();

        const dA = dayjs(combinedA, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "DD/MM/YYYY"], true);
        const dB = dayjs(combinedB, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "DD/MM/YYYY"], true);

        if (dA.isValid() && dB.isValid()) {
          return order === "asc"
            ? dA.valueOf() - dB.valueOf()
            : dB.valueOf() - dA.valueOf();
        }
      }

      // ✅ Handle numeric sorting
      const numA = parseFloat(valA);
      const numB = parseFloat(valB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return order === "asc" ? numA - numB : numB - numA;
      }

      // ✅ Default string comparison
      return order === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    setSortField(field);
    setSortOrder(order);
    setTransactions(sorted);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "";
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  const paginated = transactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      {/* 🔍 Search bar */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
      >
        <Box
          display="flex"
          gap={1}
          width={{ xs: "100%", sm: "60%" }}
          alignItems="center"
        >
          <TextField
            placeholder="Search By Name, Username, Email, Transaction ID"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{
              flexGrow: 1,
              background: "linear-gradient(to right, #f5f5f5, #bdbdbd)",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              borderRadius: 10,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="black" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Button
                    onClick={handleSearch}
                    size="small"
                    variant="text"
                    disabled={loading || !currentUser}
                  >
                    {loading ? <CircularProgress size={16} /> : "Go"}
                  </Button>
                </InputAdornment>
              ),
            }}
            disabled={!currentUser}
          />
          {activeSearch && (
            <Button variant="outlined" size="small" onClick={handleResetSearch}>
              Clear Search
            </Button>
          )}
        </Box>
      </Box>

      {/* ⚙️ Table */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
          <Typography ml={2}>Loading transactions...</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#e0f7fa" }}>
                {[
                  ["username", "USER ID"],
                  ["transid", "TRANSACTION ID"],
                  ["credit", "CREDIT"],
                  ["debit", "DEBIT"],
                  ["date", "DATE & TIME"],
                  ["remark", "REMARK"],
                ].map(([key, label]) => (
                  <TableCell
                    key={key}
                    onClick={() => handleSort(key)}
                    sx={{
                      cursor: currentUser ? "pointer" : "default",
                      fontWeight: "bold",
                      opacity: currentUser ? 1 : 0.6,
                    }}
                  >
                    {label}
                    {currentUser && getSortIcon(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.length > 0 ? (
                paginated.map((row, i) => (
                  <TableRow
                    key={row.transid || i}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "white" : "#f0f6ff",
                      opacity: currentUser ? 1 : 0.6,
                    }}
                  >
                    <TableCell>{row.username || "-"}</TableCell>
                    <TableCell>{row.transid || "-"}</TableCell>
                    <TableCell>{row.credit ?? "-"}</TableCell>
                    <TableCell>{row.debit ?? "-"}</TableCell>
                    <TableCell>
                      {row.date || "-"} {row.time ? `, ${row.time}` : ""}
                    </TableCell>
                    <TableCell>{row.remark || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography py={3} color="textSecondary">
                      {!currentUser
                        ? "Please log in to view transactions"
                        : activeSearch
                          ? "No transactions match your search"
                          : "No transactions found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {transactions.length > 0 && currentUser && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={transactions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          )}
        </TableContainer>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionTable;
